import { ObjectHelper } from "~/helpers/objectHelper";
import { Hash } from "~/interfaces/hashInterfaces";
import { ConditionalFieldDefinition } from "~/models/conditionalRules/conditionalFieldDefinition";
import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";
import ConditionalRuleSet from "~/models/conditionalRules/conditionalRuleSet";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import RelationSectionLinkedDetailView from "~/models/detailView/elements/relationSectionLinkedDetailView";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import ObjectModelConditionalFields from "~/models/objectModels/objectModelConditionalFields";
import ObjectModelConditionalRuleSet from "~/models/objectModels/objectModelConditionalRuleSet";
import { ReferencePath } from "~/models/referencePaths/referencePath";
import Selected from "../../../../formability/detailViewBuilder/store/modules/selected";
import { DetailViewContentElementType } from "../../../enums/detailViews/detailViewContentElementType";
import IRepo from "../../../interfaces/repo";
import Validatable from "../../../mixins/validatable";
import Provider from "../../../providers/new/provider";
import ElementHandler from "../../../services/elementHandler";
import Repo from "../../../state/modules/repo";
import { ISelectedProperty } from "../../../state/modules/singleSelectedProperty";
import ContentElement from "../../contentManagement/contentElement";
import DetailViewContentElementValidation from "../../navigableValidations/detailViewContentElementValidation";
import { RelationType } from "../../relationType";
import DetailView from "../detailView";
import PublishedDetailView from "../publishedDetailView";
import { DetailViewContentElementCapability } from "./detailViewContentElementCapability";
import DetailViewContentElementCapabilityManager from "./detailViewContentElementCapabilityManager";
import DetailViewContentElementDefinition from "./detailViewContentElementDefinition";
import MappedPropertyLinkedDetailView from "./mappedPropertyLinkedDetailview";

export default class DetailViewContentElement extends Validatable(ContentElement) implements IConditionalRuleContainer {
  rootModel: DetailView | PublishedDetailView;
  except = ['detailView', 'createEmbeddedObject', 'organization', 'saveTimer', '_organization'];
  help_text: string;
  label_title: string;
  action_client_id: string;
  labelTag: string = "h4";
  labelClass: string = "h4";
  visible: boolean;
  reference_path: ReferencePath

  static repo: IRepo<DetailViewContentElement> = new Repo<DetailViewContentElement>('detailViewContentElements', DetailViewContentElement, {}, new Provider(DetailViewContentElement, 'content_element', 'content_elements'));
  repo: IRepo<this, Provider<this>>;
  _contentElementHandler = new ElementHandler(this, DetailViewContentElement, 'content_elements');


  constructor() {
    super();
    this.defineProperty("label_title", this.defaultLabel);
    this.defineProperty("help_text", null);

    for (const definition of ObjectModelConditionalFields.DETAIL_VIEW_ELEMENT_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      content_elements: {type: DetailViewContentElement, relationType: RelationType.dictionary},
      mapped_property_linked_detail_views: {type: MappedPropertyLinkedDetailView, relationType: RelationType.dictionary},
      relation_section_linked_detail_views: {type: RelationSectionLinkedDetailView, relationType: RelationType.dictionary},
      reference_path: {type: ReferencePath, relationType: RelationType.singleObject},

    };
    for (const definition of ObjectModelConditionalFields.DETAIL_VIEW_ELEMENT_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: ObjectModelConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }


  createEmbeddedObject = {
    //Need to pass in content type so factory can create right model
    content_elements: (parent: object , json: object) => this.rootModel.detailViewContentElementFactory(json["content_type"]),
    mapped_property_linked_detail_views: (parent: object, json: {property_client_id: string, detail_view_id: string}) => new MappedPropertyLinkedDetailView(parent, json),
    relation_section_linked_detail_views: (parent: object, json: {related_object_model_id: string, detail_view_id: string}) => new RelationSectionLinkedDetailView(parent, json),
    reference_path: (parent: object, json: {}) => new ReferencePath(parent, json, { nameInParent: 'reference_path', isCollectionRoot: false }).loadFromJson(json),
  };

  addDefaultProperties(){
    DetailViewContentElementCapabilityManager.addDefaultProperties(this);
  }

  // ******************** VALIDATABLE******************** //
  _validation: DetailViewContentElementValidation = new DetailViewContentElementValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case 'property_id':
        return "Property to display";
      default:
        return propName;
    }
  }
  // ******************** VALIDATABLE END******************** //

  get defaultLabel(): string {
    return null
  }

  get objectModel(){
    return this.rootModel.objectModel
  }

  get publishedObjectModel() {
    return this.rootModel.published_object_model;
  }

  addToRepo(): void {
    DetailViewContentElement.repo.add(this)
  }
  get selector(): ISelectedProperty {
    return Selected.detailViewContentElement;
  }

  can(duty: DetailViewContentElementCapability): boolean {
    return DetailViewContentElementCapabilityManager.can(this.content_type, duty);
  }

  get isSelectable(): boolean {
    return this.can(DetailViewContentElementCapability.selectable);
  }

  get isSelected(): boolean {
    return Selected.detailViewContentElement.is(this);
  }

  get isLocked(): boolean {
    return this.can(DetailViewContentElementCapability.lockable) || this.parent instanceof DetailViewContentElement && this.parent.is(DetailViewContentElementType.Header)
  }
  get icon(): string {
    return DetailViewContentElementDefinition.getIcon(this.content_type);
  }

  get displayName(): string {
    return DetailViewContentElementDefinition.getDisplayName(this.content_type);
  }

  is(...contentTypes: Array<DetailViewContentElementType>): boolean {
    return contentTypes.includes(this.content_type);
  }

  isNot(...contentTypes: Array<DetailViewContentElementType>): boolean {
    return !this.is(...contentTypes);
  }


  get isFirst(): boolean {
    // header element always stays at the top
    return this.sort_order <= 1;
  }

  findNearest(contentType: DetailViewContentElementType): DetailViewContentElement {
    if (!!this.parent && this.parent instanceof DetailViewContentElement) {
      return this.parent.is(contentType) ? this.parent : this.parent.findNearest(contentType);
    }
    return null;
  }

  // ******************** IConditionalRuleContainer ******************** //

  getConditionalRuleSets(fieldName: string): Hash<ConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): ConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => y.sort_order - x.sort_order);
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): ConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty {
    return ObjectModelProperty.repo.find(id);
  }

  getReferencableControls(): ObjectModelProperty[] {
    return this.rootModel.objectModel.objectModelPropertiesArray;
  }

  // ******************** IConditionalRuleContainer END ******************** //

}